import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/atoms/Button';
import { getTestimonials } from './landingContent';
import { Testimonial } from './types';

interface TestimonialsSectionProps {
  className?: string;
}

const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
    <path d="M9.05 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.364 1.118l1.519 4.674c.3.921-.755 1.688-1.539 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.539-1.118l1.519-4.674a1 1 0 00-.364-1.118L2.076 10.1c-.783-.57-.38-1.81.588-1.81h4.915a1 1 0 00.95-.69l1.52-4.674z" />
  </svg>
);

const QuoteIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 32 32" fill="currentColor" className={className} aria-hidden="true">
    <path d="M9.333 8C5.467 8 2.667 11.2 2.667 15.067V24h8.8v-8.8H6.4c0-2.8 1.733-4.4 4.533-4.533V8H9.333zm14.4 0c-3.867 0-6.667 3.2-6.667 7.067V24h8.8v-8.8H20.8c0-2.8 1.733-4.4 4.533-4.533V8h-1.6z" />
  </svg>
);

const TestimonialCard: React.FC<{ testimonial: Testimonial; index: number }> = ({ testimonial, index }) => {
  const rating = testimonial.rating ?? 5;
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.05 }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: 'easeOut' }}
      // whileHover removed: on touch devices framer-motion treats touchstart as
      // a hover-in (pointerenter fires) and the y:-4 translation kicks in,
      // which can claim the gesture and make vertical pan-through feel broken
      // — especially on the lower half of the card where the shift is most
      // noticeable. CSS :hover still handles the desktop visual via
      // hover:shadow-* below, so desktop UX is unchanged.
      className="relative snap-start shrink-0 flex flex-col justify-between gap-5 w-[280px] max-w-[calc(100vw-3rem)] sm:w-[340px] sm:max-w-none md:w-[360px] bg-[#FCF3ED] rounded-2xl p-5 sm:p-7 shadow-[0_1px_2px_rgba(16,24,40,0.04)] hover:shadow-[0_12px_28px_-14px_rgba(91,47,217,0.20)] transition-shadow duration-300 ease-out"
    >
      <QuoteIcon className="absolute top-5 right-5 w-8 h-8 text-[#5B2FD9]/15" />

      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <StarIcon
            key={i}
            className={`w-4 h-4 ${i < rating ? 'text-[#5B2FD9]/15' : 'text-[#5B2FD9]/5'}`}
          />
        ))}
      </div>

      <p className="text-[#171D27] text-base md:text-[17px] font-medium leading-snug flex-1">
        “{testimonial.quote}”
      </p>

      <div className="flex flex-col gap-0.5 pt-4 border-t border-[#E8DFD2]">
        <span className="text-[#171D27] text-sm font-bold leading-tight truncate">
          {testimonial.name}
        </span>
        <span className="text-[#4C4E51] text-xs font-normal leading-snug truncate">
          {testimonial.title}, {testimonial.company}
        </span>
      </div>
    </motion.article>
  );
};

export const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ className }) => {
  const { t } = useTranslation('marketing');
  const testimonials = useMemo(() => getTestimonials(t), [t]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateBounds = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateBounds();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateBounds, { passive: true });
    window.addEventListener('resize', updateBounds);
    return () => {
      el.removeEventListener('scroll', updateBounds);
      window.removeEventListener('resize', updateBounds);
    };
  }, [updateBounds]);

  const scrollByCard = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const firstCard = el.querySelector('article');
    const cardWidth = firstCard ? (firstCard as HTMLElement).offsetWidth + 24 : 360;
    el.scrollBy({ left: direction === 'right' ? cardWidth : -cardWidth, behavior: 'smooth' });
  };

  return (
    <section
      className={`flex flex-col justify-center items-center gap-10 md:gap-12 w-full bg-white py-16 md:py-24 ${className || ''}`}
    >
      <div className="flex flex-col items-center gap-4 max-w-3xl text-center px-4 sm:px-6">
        <h2 className="text-dark-invirtus text-3xl sm:text-4xl md:text-5xl font-bold leading-tight tracking-tight">
          {t('testimonials.title')}
        </h2>
      </div>

      <div className="w-full max-w-screen-2xl mx-auto flex items-center gap-3 sm:gap-5 lg:gap-7 px-4 sm:px-8 lg:px-12">
        <Button
          type="icon"
          intent="light"
          size="lg"
          icon={<ArrowLeft />}
          onClick={() => scrollByCard('left')}
          disabled={atStart}
          aria-label={t('testimonials.prev')}
        />

        <div className="relative flex-1 min-w-0">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6 sm:w-10 bg-gradient-to-r from-white to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 sm:w-10 bg-gradient-to-l from-white to-transparent" />
          <div
            ref={scrollRef}
            // Default touch-action (auto): the browser does its own direction
            // detection — mostly-horizontal swipes scroll the carousel,
            // mostly-vertical swipes bubble up and scroll the page. This is
            // the standard pattern for a horizontal carousel nested in a
            // vertical page and works without any touch-action declaration.
            className="flex gap-6 overflow-x-auto sm:snap-x sm:snap-mandatory scroll-smooth overscroll-x-contain pt-3 pb-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {testimonials.map((testimonial, i) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} index={i} />
            ))}
          </div>
        </div>

        <Button
          type="icon"
          intent="light"
          size="lg"
          icon={<ArrowRight />}
          onClick={() => scrollByCard('right')}
          disabled={atEnd}
          aria-label={t('testimonials.next')}
        />
      </div>
    </section>
  );
};
